import { type Dispatch, useCallback, useEffect, useLayoutEffect, useReducer, useRef } from "react";
import type { TileAction, TileStore } from "../utils/timeSeriesTiles/store";
import { type FetchTile, type Tier, type TileRef, tileKey, type Viewport } from "../utils/timeSeriesTiles/types";

export interface UseTileLoaderArgs {
  refs: TileRef[];
  refsKey: string;
  store: TileStore;
  tier: Tier;
  viewport: Viewport;
  fetchTile: FetchTile;
  dispatch: Dispatch<TileAction>;
  concurrency: number;
  maxFutureMs?: number;
  resetKey?: string;
  refreshKey?: string;
  refreshEnabled?: boolean;
  automaticLoads?: boolean;
}

type Request = {
  ac: AbortController;
  tierId: string;
  generation: number;
  refresh: boolean;
  automatic: boolean;
  viewportKey: string;
};

/** A bounded priority pool. Refresh generations retain data and coalesce bursts per tile. */
export function useTileLoader({
  refs,
  refsKey,
  store,
  tier,
  viewport,
  fetchTile,
  dispatch,
  concurrency,
  maxFutureMs,
  resetKey,
  refreshKey,
  refreshEnabled = true,
  automaticLoads = false,
}: UseTileLoaderArgs) {
  const current = useRef({ resetKey, refreshKey, refreshEnabled });
  useLayoutEffect(() => {
    current.current = { resetKey, refreshKey, refreshEnabled };
  }, [resetKey, refreshKey, refreshEnabled]);
  const inflight = useRef(new Map<string, Request>());
  const pending = useRef(new Map<string, number>());
  const generation = useRef(0);
  const interruptedRefresh = useRef(false);
  const refreshWave = useRef<{ generation: number; seen: Set<string> } | null>(null);
  const cancelledAtViewport = useRef(new Map<string, string>());
  const previous = useRef({ resetKey, refreshKey, refreshEnabled });
  const previousViewport = useRef(`${tier.id}:${viewport.startMs}:${viewport.endMs}`);
  const [completed, pump] = useReducer((n: number) => n + 1, 0);

  const abortAll = useCallback(() => {
    for (const entry of inflight.current.values()) entry.ac.abort();
    inflight.current.clear();
    pending.current.clear();
    refreshWave.current = null;
    interruptedRefresh.current = false;
    cancelledAtViewport.current.clear();
  }, []);

  useEffect(() => {
    const viewportKey = `${tier.id}:${viewport.startMs}:${viewport.endMs}`;
    const sourceChanged = previous.current.resetKey !== resetKey;
    const refreshChanged = previous.current.refreshKey !== refreshKey;
    const paused = previous.current.refreshEnabled && !refreshEnabled;
    if (sourceChanged) abortAll();
    if (!refreshEnabled) {
      interruptedRefresh.current ||=
        pending.current.size > 0 || [...inflight.current.values()].some((job) => job.refresh);
      const cancelled = [...pending.current.keys()];
      for (const id of cancelled) cancelledAtViewport.current.set(id, previousViewport.current);
      pending.current.clear();
      refreshWave.current = null;
      for (const [id, request] of inflight.current) {
        if (!request.refresh && !(paused && request.automatic)) continue;
        request.ac.abort();
        inflight.current.delete(id);
        cancelled.push(id);
        cancelledAtViewport.current.set(
          id,
          paused && previousViewport.current === viewportKey ? viewportKey : request.viewportKey
        );
      }
      if (cancelled.length) dispatch({ type: "cancel", ids: cancelled });
    } else if ((refreshChanged || interruptedRefresh.current) && !sourceChanged) {
      interruptedRefresh.current = false;
      generation.current += 1;
      refreshWave.current = { generation: generation.current, seen: new Set() };
    }
    previous.current = {
      resetKey,
      refreshKey: refreshEnabled || sourceChanged ? refreshKey : previous.current.refreshKey,
      refreshEnabled,
    };
    previousViewport.current = viewportKey;
    const wave = refreshWave.current;
    if (wave) {
      const queued: string[] = [];
      for (const ref of refs) {
        const id = tileKey(ref.tierId, ref.index);
        if (wave.seen.has(id) || (maxFutureMs != null && ref.startMs >= maxFutureMs)) continue;
        wave.seen.add(id);
        pending.current.set(id, wave.generation);
        queued.push(id);
      }
      if (queued.length) dispatch({ type: "request", ids: queued, refresh: true });
    }

    const wantedIds = new Set(refs.map((ref) => tileKey(ref.tierId, ref.index)));
    for (const id of pending.current.keys()) {
      if (wantedIds.has(id)) continue;
      pending.current.delete(id);
      cancelledAtViewport.current.set(id, viewportKey);
      dispatch({ type: "cancel", ids: [id] });
    }
    for (const [id, request] of inflight.current) {
      if (request.tierId !== tier.id || (request.refresh && !wantedIds.has(id))) {
        request.ac.abort();
        inflight.current.delete(id);
        cancelledAtViewport.current.set(id, viewportKey);
        dispatch({ type: "cancel", ids: [id] });
      }
    }

    const missing = refs.filter((ref) => {
      if (maxFutureMs != null && ref.startMs >= maxFutureMs) return false;
      const id = tileKey(ref.tierId, ref.index);
      if (inflight.current.has(id)) return false;
      const entry = store.get(id);
      const cancelledViewport = cancelledAtViewport.current.get(id);
      if (cancelledViewport === viewportKey && !pending.current.has(id)) return false;
      return pending.current.has(id) || cancelledViewport != null || !entry || entry.status === "loading";
    });
    const center = (viewport.startMs + viewport.endMs) / 2;
    missing.sort((a, b) => Math.abs((a.startMs + a.endMs) / 2 - center) - Math.abs((b.startMs + b.endMs) / 2 - center));
    const batch = missing.slice(0, Math.max(0, concurrency - inflight.current.size));
    for (const ref of batch) {
      const id = tileKey(ref.tierId, ref.index);
      const refreshGeneration = pending.current.get(id);
      const refresh = refreshGeneration !== undefined;
      const request: Request = {
        ac: new AbortController(),
        tierId: ref.tierId,
        generation: refreshGeneration ?? generation.current,
        refresh,
        automatic: automaticLoads && refreshEnabled,
        viewportKey,
      };
      cancelledAtViewport.current.delete(id);
      inflight.current.set(id, request);
      dispatch({ type: "request", ids: [id], refresh });
      const end = maxFutureMs != null ? Math.min(ref.endMs, maxFutureMs) : ref.endMs;
      const accepted = () =>
        !request.ac.signal.aborted &&
        current.current.resetKey === resetKey &&
        inflight.current.get(id) === request &&
        (!request.automatic || current.current.refreshEnabled) &&
        (!refresh || (current.current.refreshEnabled && current.current.refreshKey === refreshKey)) &&
        (pending.current.get(id) ?? request.generation) === request.generation;
      Promise.resolve()
        .then(() => {
          if (request.ac.signal.aborted) throw new DOMException("Aborted", "AbortError");
          return fetchTile(tier, ref.startMs, end, request.ac.signal, {
            reason: refresh ? "refresh" : "load",
            isCurrent: accepted,
          });
        })
        .then((data) => {
          if (accepted()) dispatch({ type: "fulfilled", id, data, refresh });
        })
        .catch((err) => {
          if (!accepted()) return;
          const detail = err instanceof Error ? err.message : String(err);
          dispatch({
            type: "failed",
            id,
            error: new Error(`tile fetch failed (tile ${id}, tier ${tier.id}): ${detail}`),
          });
        })
        .finally(() => {
          // An old finally must never delete a replacement job after pause/source changes.
          if (inflight.current.get(id) !== request) return;
          inflight.current.delete(id);
          if (pending.current.get(id) === request.generation) pending.current.delete(id);
          if (!pending.current.size && ![...inflight.current.values()].some((job) => job.refresh)) {
            refreshWave.current = null;
          }
          pump();
        });
    }
  }, [
    refs,
    refsKey,
    store,
    fetchTile,
    tier,
    viewport,
    dispatch,
    concurrency,
    maxFutureMs,
    resetKey,
    refreshKey,
    refreshEnabled,
    automaticLoads,
    abortAll,
    completed,
  ]);

  useEffect(() => abortAll, [abortAll]);
  return { abortAll };
}
